import { blobMediaUrl } from "@/lib/blob-url"
import { goKeys as k } from "@/i18n/go-keys"
import { homeKeys as hk } from "@/i18n/home-keys"
import {
  PersonHeartIcon,
  DropletIcon,
  HeartPulseIcon,
  SearchIcon,
  ClockIcon,
  ShieldCheckIcon,
} from "@/components/ui/icons"
import {
  HeartOrganIcon,
  BloodCellOrganIcon,
  ImmuneOrganIcon,
  MetabolismOrganIcon,
} from "@/components/ui/organ-icons"

/* ── section data (keys live in the go.heart-health.* namespace) ── */

// Six reasons people check their heart, from the verified copy spec: family
// history, "my cholesterol is normal", raised BP/blood sugar, never checked, a
// scare or milestone, and wanting to stay well. Worry-first, not a diagnosis.
export const SIGNS = [
  {
    titleKey: k.heartHealthSign1Title,
    descKey: k.heartHealthSign1Desc,
    Icon: PersonHeartIcon, // heart disease in the family
  },
  {
    titleKey: k.heartHealthSign2Title,
    descKey: k.heartHealthSign2Desc,
    Icon: DropletIcon, // "my cholesterol is normal"
  },
  {
    titleKey: k.heartHealthSign3Title,
    descKey: k.heartHealthSign3Desc,
    Icon: HeartPulseIcon, // high blood pressure or blood sugar
  },
  {
    titleKey: k.heartHealthSign4Title,
    descKey: k.heartHealthSign4Desc,
    Icon: SearchIcon, // never really been checked
  },
  {
    titleKey: k.heartHealthSign5Title,
    descKey: k.heartHealthSign5Desc,
    Icon: ClockIcon, // a scare, or a milestone birthday
  },
  {
    titleKey: k.heartHealthSign6Title,
    descKey: k.heartHealthSign6Desc,
    Icon: ShieldCheckIcon, // feel fine, want to keep it that way
  },
] as const

// The four parts of cardiovascular risk we read, in spec order: lipids &
// particles, inherited risk (Lp(a)), inflammation, and metabolic co-drivers.
// Counts are CMS-derived (cardiovascular category = 11 base markers; Lp(a) 1;
// hs-CRP 1 from the inflammation category; 4 metabolic co-drivers) → 17 total.
// tileImage tiles are the shared Figma gradient tiles (icon overlaid on top);
// abstract gradients, reused as placeholders. TODO: heart-specific tiles.
export const MARKERS = [
  {
    eyebrowKey: k.heartHealthMarker1Eyebrow, // "11 markers"
    titleKey: k.heartHealthMarker1Title,
    descKey: k.heartHealthMarker1Desc,
    Icon: HeartOrganIcon, // lipids & particles
    iconClassName: "h-16 w-16",
    tileImage: "/measure-tile-lipids.png",
    chips: [k.heartHealthMarker1Chip1, k.heartHealthMarker1Chip2],
  },
  {
    eyebrowKey: k.heartHealthMarker2Eyebrow, // "1 marker"
    titleKey: k.heartHealthMarker2Title,
    descKey: k.heartHealthMarker2Desc,
    Icon: BloodCellOrganIcon, // inherited risk — Lp(a)
    iconClassName: "h-16 w-16",
    tileImage: "/measure-tile-liver.png",
    // Lp(a) is deliberately an add-on, not part of the Advanced panel — it only
    // needs measuring once in a lifetime, so it never belongs in a repeatable
    // panel. The third chip says so on the card itself (AHD-10324).
    chips: [
      k.heartHealthMarker2Chip1,
      k.heartHealthMarker2Chip2,
      k.heartHealthMarker2Chip3,
    ],
  },
  {
    eyebrowKey: k.heartHealthMarker3Eyebrow, // "1 marker"
    titleKey: k.heartHealthMarker3Title,
    descKey: k.heartHealthMarker3Desc,
    Icon: ImmuneOrganIcon, // inflammation — hs-CRP
    iconClassName: "h-16 w-16",
    tileImage: "/measure-tile-thyroid.png",
    chips: [k.heartHealthMarker3Chip1, k.heartHealthMarker3Chip2],
  },
  {
    eyebrowKey: k.heartHealthMarker4Eyebrow, // "4 markers"
    titleKey: k.heartHealthMarker4Title,
    descKey: k.heartHealthMarker4Desc,
    Icon: MetabolismOrganIcon, // metabolic co-drivers
    iconClassName: "h-20 w-20",
    tileImage: "/measure-tile-blood-sugar.png",
    chips: [k.heartHealthMarker4Chip1, k.heartHealthMarker4Chip2],
  },
] as const

// Three cross-marker stories (verified copy spec). Each shows several markers,
// often across groups, that read together tell a story a single value misses.
// Values + badges match the verification report's plausibility sweep exactly.
// Health axis: green (optimal) left → terracotta (high) right; the dot is where
// the illustrative value falls.
export const INSIGHTS_V2 = [
  {
    titleKey: k.heartHealthStory1Title,
    leadKey: k.heartHealthStory1Lead,
    bodyKey: k.heartHealthStory1Body,
    readings: [
      {
        markerKey: k.heartHealthMkLdl,
        groupKey: k.heartHealthGroupLipids,
        value: "3.1",
        unit: "mmol/l",
        badgeKey: k.heartHealthBadgeNormal,
        tone: "green",
        zones: [
          { c: "green", w: 54 },
          { c: "sand", w: 22 },
          { c: "terra", w: 24 },
        ],
        dot: 44,
      },
      {
        markerKey: k.heartHealthMkApoB,
        groupKey: k.heartHealthGroupLipids,
        value: "1.15",
        unit: "g/l",
        badgeKey: k.heartHealthBadgeRaised,
        tone: "terra",
        zones: [
          { c: "green", w: 50 },
          { c: "sand", w: 24 },
          { c: "terra", w: 26 },
        ],
        dot: 74,
      },
      {
        markerKey: k.heartHealthMkNonHdl,
        groupKey: k.heartHealthGroupLipids,
        value: "3.6",
        unit: "mmol/l",
        badgeKey: k.heartHealthBadgeBorderline,
        tone: "sand",
        zones: [
          { c: "green", w: 52 },
          { c: "sand", w: 24 },
          { c: "terra", w: 24 },
        ],
        dot: 60,
      },
    ],
  },
  {
    titleKey: k.heartHealthStory2Title,
    leadKey: k.heartHealthStory2Lead,
    bodyKey: k.heartHealthStory2Body,
    readings: [
      {
        markerKey: k.heartHealthMkLdl,
        groupKey: k.heartHealthGroupLipids,
        value: "2.8",
        unit: "mmol/l",
        badgeKey: k.heartHealthBadgeNormal,
        tone: "green",
        zones: [
          { c: "green", w: 54 },
          { c: "sand", w: 22 },
          { c: "terra", w: 24 },
        ],
        dot: 40,
      },
      {
        markerKey: k.heartHealthMkLpa,
        groupKey: k.heartHealthGroupInherited,
        value: "320",
        unit: "nmol/l",
        badgeKey: k.heartHealthBadgeHigh,
        tone: "terra",
        zones: [
          { c: "green", w: 52 },
          { c: "sand", w: 22 },
          { c: "terra", w: 26 },
        ],
        dot: 86,
      },
      {
        markerKey: k.heartHealthMkApoB,
        groupKey: k.heartHealthGroupLipids,
        value: "0.9",
        unit: "g/l",
        badgeKey: k.heartHealthBadgeNormal,
        tone: "green",
        zones: [
          { c: "green", w: 50 },
          { c: "sand", w: 24 },
          { c: "terra", w: 26 },
        ],
        dot: 36,
      },
    ],
  },
  {
    titleKey: k.heartHealthStory3Title,
    leadKey: k.heartHealthStory3Lead,
    bodyKey: k.heartHealthStory3Body,
    readings: [
      {
        markerKey: k.heartHealthMkLdl,
        groupKey: k.heartHealthGroupLipids,
        value: "2.4",
        unit: "mmol/l",
        badgeKey: k.heartHealthBadgeNormal,
        tone: "green",
        zones: [
          { c: "green", w: 54 },
          { c: "sand", w: 22 },
          { c: "terra", w: 24 },
        ],
        dot: 32,
      },
      {
        markerKey: k.heartHealthMkHsCrp,
        groupKey: k.heartHealthGroupInflammation,
        value: "3.4",
        unit: "mg/l",
        badgeKey: k.heartHealthBadgeRaised,
        tone: "terra",
        zones: [
          { c: "green", w: 52 },
          { c: "sand", w: 24 },
          { c: "terra", w: 24 },
        ],
        dot: 74,
      },
      {
        markerKey: k.heartHealthMkTriglycerides,
        groupKey: k.heartHealthGroupLipids,
        value: "1.4",
        unit: "mmol/l",
        badgeKey: k.heartHealthBadgeNormal,
        tone: "green",
        zones: [
          { c: "green", w: 56 },
          { c: "sand", w: 22 },
          { c: "terra", w: 22 },
        ],
        dot: 40,
      },
    ],
  },
] as const

/**
 * How-it-works steps. `imaging: true` marks a step that only exists in markets
 * where the imaging arm is bookable — the client drops it and renumbers the
 * rest, so a blood-only market never advertises a scan it can't sell.
 */
export const PROCESS = [
  {
    titleKey: k.heartHealthProcessStep1Title,
    descKey: k.heartHealthProcessStep1Desc,
    image: blobMediaUrl("/images/immunity/immunity-step-book-2.jpg"), // TODO: heart imagery
  },
  {
    titleKey: k.heartHealthProcessStep2Title,
    descKey: k.heartHealthProcessStep2Desc,
    image: blobMediaUrl("/images/immunity/immunity-step-blood-draw.jpg"), // TODO: heart imagery
  },
  {
    // Optional imaging visit — the upsell arm (full-body MRI + optional coronary
    // calcium CT), sequenced before the report so the full-picture path reads as
    // one flow (ported from /go/metabolism).
    imaging: true,
    titleKey: k.heartHealthProcessStep3Title,
    descKey: k.heartHealthProcessStep3Desc,
    // Step 3 leads with the coronary calcium score (a low-dose CT), so the tile
    // shows the CT scanner, not an MRI. (MRI is the whole-body add-on named in
    // the copy; the imaging step's hero image is the heart-specific CT.)
    image: blobMediaUrl("/images/steps/calcium/02-ct-scan.jpeg"),
  },
  {
    titleKey: k.heartHealthProcessStep4Title,
    descKey: k.heartHealthProcessStep4Desc,
    image: blobMediaUrl("/images/mri/walkthrough/step-report-v2.png"), // TODO: heart imagery
  },
] as const

// FAQ set lifted verbatim from /go/bloodtest (home namespace) so the heart LP
// shares the same blood-test answers + layout.
export const FAQ_KEYS = [
  { q: hk.faqQ1, a: hk.faqA1 },
  { q: hk.faqQ2, a: hk.faqA2 },
  { q: hk.faqQ4, a: hk.faqA4 },
  { q: hk.faqQ5, a: hk.faqA5 },
  { q: hk.faqQ6, a: hk.faqA6 },
  { q: hk.faqQ8, a: hk.faqA8 },
  { q: hk.faqQ9, a: hk.faqA9 },
] as const
